import { Alert, Button, Group, List, SimpleGrid, Stack } from '@mantine/core';
import { IconArrowLeft, IconPrinter, IconReceipt } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { odpriPdf } from '../api/pdf';
import { sporocilo } from '../api/napaka';
import { datum, denar } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { TabelaArtiklov, TabelaStoritev } from '../postavitev/Postavke';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';

const ime = (o?: { ime?: string; priimek?: string }) => [o?.ime, o?.priimek].filter(Boolean).join(' ');

/**
 * Podrobnosti predračuna: podatki, storitve, artikli, »Natisni« (PDF); predračun, ki
 * še ni vnesen v račun, se lahko izda kot račun.
 */
export function PodrobnostiPredracuna() {
  const { id } = useParams();
  const [napakaPdf, setNapakaPdf] = useState<string | null>(null);
  const [tiskam, setTiskam] = useState(false);
  const poizvedba = useQuery({
    queryKey: ['predracun', id],
    queryFn: ({ signal }) => podatki(api.GET('/api/predracuni/{id}', { signal, params: { path: { id: Number(id) } } })),
  });
  const p = poizvedba.data;
  if (!p) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Predračuna ni bilo mogoče naložiti"
      />
    );
  }

  const natisni = async () => {
    setNapakaPdf(null);
    setTiskam(true);
    try {
      await odpriPdf(api.GET('/api/predracuni/{id}/pdf', { params: { path: { id: p.id! } }, parseAs: 'blob' }));
    } catch (e) {
      setNapakaPdf(sporocilo(e));
    } finally {
      setTiskam(false);
    }
  };

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/predracuni"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Predračuni
      </Button>
      <GlavaZaslona
        naslov={`Predračun ${p.stPredracuna ?? ''}`}
        opis={ime(p.kupec)}
        dejanja={
          <Group gap="xs">
            {!p.stRacuna && (
              <Button
                variant="default"
                leftSection={<IconReceipt size={18} />}
                component={Link}
                to={`/racuni/nov?predracun=${p.id}`}
              >
                Izdaj račun
              </Button>
            )}
            <Button leftSection={<IconPrinter size={18} />} onClick={() => void natisni()} loading={tiskam}>
              Natisni
            </Button>
          </Group>
        }
      />
      {napakaPdf && (
        <Alert color="red" variant="light" mb="md" role="alert">
          {napakaPdf}
        </Alert>
      )}
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Kartica naslov="Podatki">
            <Podatki
              vrstice={[
                ['Številka', p.stPredracuna],
                ['Ustvarjen', datum(p.datum)],
                ['Potek', datum(p.veljavnoDo)],
                ['Ref. za plačilo', p.referencaZaPlacilo],
                ['Koda namena', p.paymentClassification || 'GDSV'],
                ['Znesek artiklov', denar(p.znesekArtiklov)],
                ['Znesek storitev', denar(p.znesekStoritev)],
                ['Skupaj', <b key="s">{denar(p.znesek)}</b>],
                ['Kupec', ime(p.kupec)],
                ['Uporabnik', ime(p.uporabnikVozila)],
                ['V račun', p.stRacuna],
              ]}
            />
          </Kartica>
          <Kartica naslov="Storitve">
            <TabelaStoritev storitve={p.storitve ?? []} />
          </Kartica>
        </SimpleGrid>
        <Kartica naslov="Artikli">
          <TabelaArtiklov vozila={p.vozila ?? []} />
        </Kartica>
        {(p.klavzule ?? []).length > 0 && (
          <Kartica naslov="Klavzule">
            <List size="sm" spacing={4}>
              {(p.klavzule ?? []).map((k) => (
                <List.Item key={k.id}>{k.besedilo}</List.Item>
              ))}
            </List>
          </Kartica>
        )}
      </Stack>
      <Group mt="md" />
    </ZaslonSeznama>
  );
}
